import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Download, FileKey2, ShieldCheck, ShieldX, Upload } from 'lucide-react';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { friendlyError } from '@/lib/errors';
import { downloadBytes } from '@/lib/printForm';
import { confirmDialog } from '@/lib/dialogs';
import { useToastStore } from '@/store/toastStore';

// ============================================================================
// ЭЦП: блок «Подписи» в предпросмотре файла (SSoT — сервер, sdk.signature):
// список подписей (версия, подписант, формат, статус: stored/verified/failed/
// revoked), скачивание .sig, прикрепление ВНЕШНЕЙ подписи (.sig из КриптоПро/
// Контур — сценарий «внешняя подпись»), отзыв (только админ).
// Криптопроверка ГОСТ выполняется внешним слоем — статус 'stored' честно
// означает «проверка не выполнялась».
// ============================================================================

interface SigInfo {
  id: string;
  version: number;
  signerName: string;
  sigFormat: string;
  sigSize: number;
  status: string;
  verifyError: string;
  comment: string;
  createdAt: string;
}

const STATUS_LABEL: Record<string, string> = {
  stored: 'принята (не проверена)',
  verified: 'подпись верна',
  failed: 'проверка не прошла',
  revoked: 'отозвана',
};
const STATUS_CLASS: Record<string, string> = {
  stored: 'border-border bg-muted text-muted-foreground',
  verified: 'border-green-500/25 bg-green-500/10 text-green-600',
  failed: 'border-red-500/25 bg-red-500/10 text-red-600',
  revoked: 'border-amber-500/25 bg-amber-500/10 text-amber-600',
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('не удалось прочитать файл подписи'));
    reader.readAsDataURL(file);
  });
}

export function FileSignatures({ fileId, fileName }: { fileId: string; fileName: string }) {
  const [items, setItems] = useState<SigInfo[] | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const uploadRef = useRef<HTMLInputElement | null>(null);

  const isAdmin = (() => {
    const roles = useAuthStore.getState().user?.roles || [];
    return roles.includes('admin') || roles.includes('global:admin');
  })();

  const load = useCallback(async () => {
    try {
      const r: any = await getSDK().signature.listSignatures({ fileId });
      setItems((r?.items || []).map((s: any) => ({
        id: String(s.id || ''),
        version: Number(s.version || 0),
        signerName: String(s.signerName || ''),
        sigFormat: String(s.sigFormat || ''),
        sigSize: Number(s.sigSize || 0),
        status: String(s.status || 'stored'),
        verifyError: String(s.verifyError || ''),
        comment: String(s.comment || ''),
        createdAt: String(s.createdAt || ''),
      })));
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
      setItems([]);
    }
  }, [fileId]);

  useEffect(() => { void load(); }, [load]);

  const downloadSig = async (id: string, version: number) => {
    setBusy(true);
    try {
      const r: any = await getSDK().signature.getSignature({ id });
      const dot = fileName.lastIndexOf('.');
      const base = dot > 0 ? fileName.slice(0, dot) : fileName;
      downloadBytes(`${base} (v${version}).sig`, 'application/octet-stream', String(r?.sigData || ''));
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const onPickSig = async (f: File | null) => {
    if (!f) return;
    setBusy(true);
    try {
      const b64 = await fileToBase64(f);
      await getSDK().signature.attachSignature({
        fileId,
        sigData: b64,
        sigFormat: 'CAdES-BES',
        comment: 'внешняя подпись: ' + f.name,
      } as never);
      await load();
      setOpen(true);
      useToastStore.getState().showSuccess('Подпись принята (статус: принята, не проверена)');
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  const revoke = async (id: string) => {
    if (!await confirmDialog('Отозвать подпись? Действие необратимо (запись остаётся в реестре).')) return;
    setBusy(true);
    try {
      await getSDK().signature.setSignatureStatus({ id, status: 'revoked', verifyError: 'отозвана пользователем' } as never);
      await load();
      useToastStore.getState().showSuccess('Подпись отозвана');
    } catch (e) {
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  if (items === null) return null;

  return (
    <div className="mb-2 rounded border bg-muted/20">
      <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] text-muted-foreground">
        <button type="button" className="flex items-center gap-1.5 hover:text-foreground" onClick={() => setOpen((o) => !o)}>
          {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          <FileKey2 className="h-3 w-3" />
          Подписи ({items.length})
        </button>
        <label className="ml-auto flex cursor-pointer items-center gap-1 rounded border px-1.5 py-0.5 hover:bg-accent" title="Прикрепить .sig (внешняя подпись из КриптоПро/Контур)">
          <Upload className="h-3 w-3" /> Прикрепить .sig
          <input
            ref={uploadRef}
            type="file"
            accept=".sig,.p7s,.p7z,application/pkcs7-signature,application/octet-stream"
            className="hidden"
            disabled={busy}
            onChange={(e) => void onPickSig(e.target.files?.[0] || null)}
          />
        </label>
      </div>
      {open && (
        <div className="space-y-1 px-2 pb-2">
          {items.length === 0 && <div className="text-[11px] text-muted-foreground">Подписей нет — прикрепите .sig для версии файла.</div>}
          {items.map((s) => (
            <div key={s.id} className="flex items-center gap-2 text-[11px]">
              <span className="w-8 font-medium">v{s.version}</span>
              <span className="truncate">{s.signerName || '—'}</span>
              <span className="text-[10px] text-muted-foreground">{s.sigFormat}</span>
              <span className="text-[10px] text-muted-foreground">{s.createdAt ? new Date(s.createdAt).toLocaleString('ru-RU') : ''}</span>
              <span className={`rounded border px-1.5 py-px text-[10px] ${STATUS_CLASS[s.status] || ''}`} title={s.verifyError || s.comment}>
                {s.status === 'verified' ? <ShieldCheck className="mr-0.5 inline h-3 w-3" /> : s.status === 'failed' || s.status === 'revoked' ? <ShieldX className="mr-0.5 inline h-3 w-3" /> : null}
                {STATUS_LABEL[s.status] || s.status}
              </span>
              <span className="ml-auto flex gap-1">
                <button
                  type="button"
                  className="rounded border px-1.5 py-0.5 hover:bg-accent disabled:opacity-50"
                  disabled={busy}
                  title="Скачать .sig"
                  onClick={() => void downloadSig(s.id, s.version)}
                >
                  <Download className="h-3 w-3" />
                </button>
                {isAdmin && s.status !== 'revoked' && (
                  <button
                    type="button"
                    className="rounded border border-amber-500/30 px-1.5 py-0.5 text-amber-600 hover:bg-amber-500/10 disabled:opacity-50"
                    disabled={busy}
                    title="Отозвать подпись (необратимо)"
                    onClick={() => void revoke(s.id)}
                  >
                    <ShieldX className="h-3 w-3" />
                  </button>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
